import { defineComponent as _defineComponent } from 'vue'
import {
    ref,
    onBeforeUnmount,
    onMounted,
    watch,
    nextTick,
    computed,
} from 'vue';

interface Position {
    top: number;
    left: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BasePopup',
  props: {
    value: { type: Boolean },
    position: { default: 'bottom' },
    transitionName: { default: 'fade' },
    width: null,
    teleportToBody: { type: Boolean, default: false },
    disabled: { type: Boolean },
    widthFactor: { default: 1 }
  },
  emits: ['update:value'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { value?: boolean, position: 'top' | 'right' | 'bottom' | 'left', transitionName: string, width?: string, teleportToBody: boolean, disabled?: boolean, widthFactor: number }





const activatorRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const contentPosition = ref<Position>({ top: 0, left: 0 });
const activatorWidth = ref<string>('auto');
const currentPosition = ref(props.position);

const isHorizontalPosition = computed(() =>
    ['left', 'right'].includes(currentPosition.value),
);

const isVerticalPosition = computed(() =>
    ['top', 'bottom'].includes(currentPosition.value),
);

const handleActivatorClick = () => {
    if (!props.disabled) {
        emits('update:value', !props.value);
    }
};

const handleClickOutside = (event: MouseEvent) => {
    if (
        props.value &&
        !activatorRef.value?.contains(event.target as Node) &&
        !contentRef.value?.contains(event.target as Node)
    ) {
        emits('update:value', false);
    }
};

const contentStyle = computed(() => {
    const style: Record<string, string> = {};

    if (props.teleportToBody) {
        style.top = `${contentPosition.value.top}px`;
        style.left = `${contentPosition.value.left}px`;
    }

    if (props.width) {
        style.width = props.width;
        return style;
    }

    if (isVerticalPosition.value) {
        style.width = activatorWidth.value;
    } else if (isHorizontalPosition.value) {
        style.width = 'max-content';
    }

    return style;
});

const calculateBestPosition = (activatorRect: DOMRect, contentRect: DOMRect) => {
    const positions = ['top', 'bottom', 'left', 'right'] as const;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const availablePositions = positions.filter(pos => {
        const testPosition = getPositionCoords(pos, activatorRect, contentRect);
        return (
            testPosition.top >= 0 &&
            testPosition.left >= 0 &&
            testPosition.top + contentRect.height <= viewportHeight &&
            testPosition.left + contentRect.width <= viewportWidth
        );
    });

    if (availablePositions.includes(props.position)) {
        return props.position;
    }

    const oppositePosition = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
    }[props.position];

    if (availablePositions.includes(oppositePosition)) {
        return oppositePosition;
    }

    return availablePositions[0] || props.position;
};

const getPositionCoords = (position: string, activatorRect: DOMRect, contentRect: DOMRect) => {
    switch (position) {
    case 'top':
        return {
            top: activatorRect.top - contentRect.height,
            left: activatorRect.left,
        };
    case 'bottom':
        return {
            top: activatorRect.bottom,
            left: activatorRect.left,
        };
    case 'left':
        return {
            top: activatorRect.top + (activatorRect.height - contentRect.height) / 2,
            left: activatorRect.left - contentRect.width,
        };
    case 'right':
        return {
            top: activatorRect.top + (activatorRect.height - contentRect.height) / 2,
            left: activatorRect.right,
        };
    default:
        return { top: 0, left: 0 };
    }
};

const calculatePosition = (): Position => {
    if (!activatorRef.value || !contentRef.value) {
        return { top: 0, left: 0 };
    }

    const activatorRect = activatorRef.value.getBoundingClientRect();
    const contentRect = contentRef.value.getBoundingClientRect();

    currentPosition.value = calculateBestPosition(activatorRect, contentRect);

    if (isVerticalPosition.value) {
        activatorWidth.value = `${activatorRect.width * props.widthFactor}px`;
    }

    let { top, left } = getPositionCoords(currentPosition.value, activatorRect, contentRect);

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (isVerticalPosition.value) {
        left = Math.max(0, Math.min(left, viewportWidth - contentRect.width));
        if ((top + contentRect.height) > viewportHeight) {
            top = viewportHeight - contentRect.height;
        }
    } else {
        top = Math.max(0, Math.min(top, viewportHeight - contentRect.height));
    }

    if (!props.teleportToBody && activatorRef.value.parentElement) {
        const parentRect = activatorRef.value.parentElement.getBoundingClientRect();
        top -= parentRect.top;
        left -= parentRect.left;
    }

    return { top, left };
};

const updatePosition = () => {
    if (!props.value) return;

    nextTick(() => {
        if (!contentRef.value) return;

        const newPosition = calculatePosition();
        contentPosition.value = newPosition;

        if (props.teleportToBody) {
            contentRef.value.style.top = `${newPosition.top}px`;
            contentRef.value.style.left = `${newPosition.left}px`;
        } else {
            contentRef.value.style.top = `${newPosition.top}px`;
            contentRef.value.style.left = `${newPosition.left}px`;
        }
    });
};

const teleportContentToBody = () => {
    if (contentRef.value && !contentRef.value.parentNode?.isEqualNode(document.body)) {
        document.body.appendChild(contentRef.value);
    }
};

const removeContentFromBody = () => {
    if (contentRef.value && contentRef.value.parentNode?.isEqualNode(document.body)) {
        document.body.removeChild(contentRef.value);
    }
};

const handleScrollAndResize = () => {
    updatePosition();
};

watch(() => props.value, (newValue: boolean) => {
    if (newValue) {
        if (props.teleportToBody) {
            nextTick(() => {
                teleportContentToBody();
            });
        }
        updatePosition();
    } else if (props.teleportToBody) {
        removeContentFromBody();
    }
}, { deep: true, immediate: true });

onMounted(() => {
    window.addEventListener('resize', handleScrollAndResize);
    window.addEventListener('scroll', handleScrollAndResize, true);
});

onBeforeUnmount(() => {
    if (props.teleportToBody) {
        removeContentFromBody();
    }
    window.removeEventListener('resize', handleScrollAndResize);
    window.removeEventListener('scroll', handleScrollAndResize, true);
});

expose({
    updatePosition,
});

return { __sfc: true,props, emits, activatorRef, contentRef, contentPosition, activatorWidth, currentPosition, isHorizontalPosition, isVerticalPosition, handleActivatorClick, handleClickOutside, contentStyle, calculateBestPosition, getPositionCoords, calculatePosition, updatePosition, teleportContentToBody, removeContentFromBody, handleScrollAndResize }
}

})